/**
 * Accessibility utility functions
 * Requirements: 15.7 (WCAG AA compliance)
 */

/**
 * Convert hex color to RGB
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * Calculate relative luminance of a color
 * https://www.w3.org/TR/WCAG20/#relativeluminancedef
 */
function getLuminance(r: number, g: number, b: number): number {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    const sRGB = c / 255;
    return sRGB <= 0.03928 ? sRGB / 12.92 : Math.pow((sRGB + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Calculate contrast ratio between two colors
 * https://www.w3.org/TR/WCAG20/#contrast-ratiodef
 */
export function getContrastRatio(color1: string, color2: string): number {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);

  if (!rgb1 || !rgb2) {
    return 0;
  }

  const lum1 = getLuminance(rgb1.r, rgb1.g, rgb1.b);
  const lum2 = getLuminance(rgb2.r, rgb2.g, rgb2.b);

  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);

  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Check if color combination meets WCAG AA standards
 * Normal text: 4.5:1
 * Large text (18pt+ or 14pt+ bold): 3:1
 */
export function meetsWCAGAA(
  foreground: string,
  background: string,
  isLargeText: boolean = false
): boolean {
  const ratio = getContrastRatio(foreground, background);
  const minRatio = isLargeText ? 3 : 4.5;
  return ratio >= minRatio;
}

/**
 * Check if color combination meets WCAG AAA standards
 * Normal text: 7:1
 * Large text: 4.5:1
 */
export function meetsWCAGAAA(
  foreground: string,
  background: string,
  isLargeText: boolean = false
): boolean {
  const ratio = getContrastRatio(foreground, background);
  const minRatio = isLargeText ? 4.5 : 7;
  return ratio >= minRatio;
}

/**
 * Get a contrasting text color (black or white) for a given background
 */
export function getContrastingTextColor(backgroundColor: string): string {
  const rgb = hexToRgb(backgroundColor);
  if (!rgb) return '#000000';

  const luminance = getLuminance(rgb.r, rgb.g, rgb.b);
  // Use white text for dark backgrounds, black for light backgrounds
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}

/**
 * Validate brand colors for accessibility
 */
export function validateBrandColors(
  primaryColor: string,
  accentColor: string
): {
  valid: boolean;
  errors: string[];
  warnings: string[];
} {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check primary color against white background
  const primaryOnWhite = getContrastRatio(primaryColor, '#FFFFFF');
  if (primaryOnWhite < 4.5) {
    errors.push(
      `Primary color contrast ratio with white background is ${primaryOnWhite.toFixed(
        2
      )}:1 (minimum 4.5:1 required for WCAG AA)`
    );
  }

  // Check accent color against white background
  const accentOnWhite = getContrastRatio(accentColor, '#FFFFFF');
  if (accentOnWhite < 3) {
    warnings.push(
      `Accent color contrast ratio with white background is ${accentOnWhite.toFixed(
        2
      )}:1 (minimum 3:1 recommended for large text)`
    );
  }

  // Check if primary and accent colors have enough contrast with each other
  const primaryVsAccent = getContrastRatio(primaryColor, accentColor);
  if (primaryVsAccent < 3) {
    warnings.push(
      `Primary and accent colors have low contrast (${primaryVsAccent.toFixed(
        2
      )}:1). Consider using more distinct colors.`
    );
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Generate accessible focus styles
 */
export function getFocusStyles(color: string = '#01178E') {
  return {
    outline: `2px solid ${color}`,
    outlineOffset: '2px',
    borderRadius: '4px',
  };
}

/**
 * Skip to main content link for keyboard navigation
 */
export const skipToMainContentStyles = {
  position: 'absolute' as const,
  left: '-9999px',
  zIndex: 999,
  padding: '1rem',
  backgroundColor: '#01178E',
  color: '#FFFFFF',
  textDecoration: 'none',
  borderRadius: '0 0 4px 0',
  '&:focus': {
    left: 0,
    top: 0,
  },
};
